import {
  activeCountrySubscriptionsSchema,
  addCountrySubscriptionSchema,
  checkoutResponseSchema,
  countryProductCatalogResponseSchema,
  countryProductPriceResponseSchema,
  countryProductResponseSchema,
  countrySubscriptionResponseSchema,
  createCountryCheckoutSchema,
  createSubscriptionSchema,
  planTierEnum,
  planTypeEnum,
  subscriptionResponseSchema,
  subscriptionSourceEnum,
  subscriptionStatusEnum,
  subscriptionTypeEnum,
  syncCountryProductsResponseSchema,
  updateCountryProductSchema,
  updateSubscriptionSchema
} from "../chunk-PCIRJ63C.mjs";
import "../chunk-DS3TDJHF.mjs";
import "../chunk-2USE66YR.mjs";

// src/account-api/routes.ts
var accountApiRoutes = {
  subscriptions: {
    /** Get tenant-level subscription */
    get: (tenantId) => `/tenants/${tenantId}/subscription`,
    /** Get effective subscription for an organization (inherits from tenant if no org-specific subscription) */
    getForOrg: (tenantId, organizationSlug) => `/tenants/${tenantId}/organizations/${organizationSlug}/subscription`
  },
  // Internal service-to-service endpoints
  internal: {
    /** Get all active country subscriptions grouped by tenant (for worker sync jobs) */
    activeCountries: () => `/internal/subscriptions/active-countries`
  },
  // Admin subscription management endpoints
  admin: {
    subscriptions: {
      /** Create a new subscription (admin only) */
      create: () => `/admin/subscriptions`,
      /** Get subscription by tenant slug (admin only) */
      get: (tenantSlug) => `/admin/subscriptions/${tenantSlug}`,
      /** Update subscription (admin only) */
      update: (tenantSlug) => `/admin/subscriptions/${tenantSlug}`,
      /** Delete subscription (admin only) */
      delete: (tenantSlug) => `/admin/subscriptions/${tenantSlug}`,
      /** List all subscriptions (admin only) */
      list: () => `/admin/subscriptions`,
      // Country subscription management
      countries: {
        /** Add a country subscription */
        add: (tenantSlug) => `/admin/subscriptions/${tenantSlug}/countries`,
        /** Remove a country subscription */
        remove: (tenantSlug, countrySlug) => `/admin/subscriptions/${tenantSlug}/countries/${countrySlug}`,
        /** List country subscriptions */
        list: (tenantSlug) => `/admin/subscriptions/${tenantSlug}/countries`
      }
    }
  },
  // Self-service checkout endpoints
  checkout: {
    /** Create a Chargebee checkout for a country subscription */
    createCountry: (tenantSlug) => `/checkout/${tenantSlug}/country`,
    /** Cancel a Chargebee country subscription */
    cancelCountry: (tenantSlug, countrySlug) => `/checkout/${tenantSlug}/country/${countrySlug}/cancel`,
    /** Reactivate a cancelled Chargebee country subscription */
    reactivateCountry: (tenantSlug, countrySlug) => `/checkout/${tenantSlug}/country/${countrySlug}/reactivate`
  },
  // Event log endpoints
  eventLogs: {
    /** List event logs for a tenant's subscription activity */
    list: (tenantSlug) => `/tenants/${tenantSlug}/event-logs`
  },
  // Product catalog endpoints (tenant-ui — active products only)
  products: {
    /** List available country products from Chargebee catalog */
    listCountries: () => `/products/countries`
  },
  // Country product catalog management (admin)
  countryProducts: {
    /** List all country products with prices */
    list: () => `/country-products`,
    /** Get single country product by slug */
    get: (countrySlug) => `/country-products/${countrySlug}`,
    /** Update country product (toggle active, rename) */
    update: (countrySlug) => `/country-products/${countrySlug}`,
    /** Sync country products from Chargebee */
    sync: () => `/country-products/sync`
  },
  // API Client management (admin)
  apiClients: {
    /** Create a new API client */
    create: () => `/admin/api-clients`,
    /** List all API clients */
    list: () => `/admin/api-clients`,
    /** Get API client by ID */
    get: (id) => `/admin/api-clients/${id}`,
    /** Update API client */
    update: (id) => `/admin/api-clients/${id}`,
    /** Delete API client */
    delete: (id) => `/admin/api-clients/${id}`,
    // Country subscription management for API clients
    countries: {
      /** Add country to API client */
      add: (id) => `/admin/api-clients/${id}/countries`,
      /** List countries for API client */
      list: (id) => `/admin/api-clients/${id}/countries`,
      /** Remove country from API client */
      remove: (id, countrySlug) => `/admin/api-clients/${id}/countries/${countrySlug}`
    },
    // Internal endpoints (service-to-service)
    internal: {
      /** Get subscribed countries for an API client */
      subscribedCountries: (id) => `/internal/api-clients/${id}/subscribed-countries`
    }
  },
  // Webhook endpoints
  webhooks: {
    /** Chargebee webhook receiver */
    chargebee: () => `/webhooks/chargebee`
  }
};

// src/account-api/dto/api-client.schema.ts
import { z } from "zod";
var apiClientStatusEnum = z.enum(["active", "suspended"]);
var apiClientResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  companyName: z.string(),
  contactEmail: z.string().email(),
  tenantSlug: z.string().nullable(),
  status: apiClientStatusEnum,
  subscribedCountries: z.array(z.string()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createApiClientSchema = z.object({
  name: z.string().min(1).max(255),
  companyName: z.string().min(1).max(255),
  contactEmail: z.string().email(),
  tenantSlug: z.string().min(1).nullable().optional()
});
var updateApiClientSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  companyName: z.string().min(1).max(255).optional(),
  contactEmail: z.string().email().optional(),
  status: apiClientStatusEnum.optional()
});
var addApiClientCountrySchema = z.object({
  countrySlug: z.string().min(1)
});
var apiClientCountryResponseSchema = z.object({
  id: z.string().uuid(),
  countrySlug: z.string(),
  status: z.string(),
  source: z.string(),
  createdAt: z.coerce.date()
});
var apiClientSubscribedCountriesSchema = z.object({
  apiClientId: z.string().uuid(),
  countries: z.array(z.string())
});
export {
  accountApiRoutes,
  activeCountrySubscriptionsSchema,
  addApiClientCountrySchema,
  addCountrySubscriptionSchema,
  apiClientCountryResponseSchema,
  apiClientResponseSchema,
  apiClientStatusEnum,
  apiClientSubscribedCountriesSchema,
  checkoutResponseSchema,
  countryProductCatalogResponseSchema,
  countryProductPriceResponseSchema,
  countryProductResponseSchema,
  countrySubscriptionResponseSchema,
  createApiClientSchema,
  createCountryCheckoutSchema,
  createSubscriptionSchema,
  planTierEnum,
  planTypeEnum,
  subscriptionResponseSchema,
  subscriptionSourceEnum,
  subscriptionStatusEnum,
  subscriptionTypeEnum,
  syncCountryProductsResponseSchema,
  updateApiClientSchema,
  updateCountryProductSchema,
  updateSubscriptionSchema
};
